import React from "react";
import {
  Container,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  LocalFireDepartment,
  Description,
  EmojiEmotions,
  Build,
  Warning,
  Business,
  Phone,
  Email,
  LocationOn,
  CalendarToday,
  Person,
} from "@mui/icons-material";

const DryerVentCleaning: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      {/* Blog Header */}
      <div style={{ maxWidth: '800px', margin: '0 auto', marginBottom: '3rem' }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            lineHeight: { xs: 1.3, sm: 1.4 },
            mb: { xs: 2, sm: 3 }
          }}
        >
          Why Annual Dryer Vent Cleaning is a Must for Multi-Unit Property
          Managers in Portland
        </Typography>

        {/* Author and Date Info */}
        <div 
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '1.5rem',
            marginBottom: '2rem',
            flexDirection: isMobile ? 'column' : 'row'
          }}
        >
          <Avatar sx={{ 
            mr: { xs: 0, sm: 2 },
            mb: { xs: 1, sm: 0 },
            width: { xs: 48, sm: 40 },
            height: { xs: 48, sm: 40 }
          }}>
            <Person />
          </Avatar>
          <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Cody Salane
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Published on January 10, 2025 • 8 min read
            </Typography>
          </div>
        </div>
        
        <Divider sx={{ my: 3 }} />
      </div>

      {/* Introduction */}
      <div style={{ maxWidth: '800px', margin: '0 auto', marginBottom: '3rem' }}>
        <Typography 
          variant="body1" 
          paragraph 
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.8,
            px: { xs: 1, sm: 0 }
          }}
        >
          As a property manager overseeing multi-unit housing, your job isn't
          just about keeping tenants happy. You're also responsible for
          protecting your buildings, reducing liability, and staying compliant
          with local codes. One critical but often-overlooked part of that
          responsibility? Annual dryer vent cleaning.
        </Typography>
      </div>

      <Grid container spacing={{ xs: 2, sm: 4 }} sx={{ maxWidth: '1200px', mx: 'auto' }}>
        {/* Fire Safety Section */}
        <Grid item xs={12} md={6}>
          <div style={{ 
            padding: isMobile ? '1rem' : '1.5rem',
            height: '100%',
            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <LocalFireDepartment color="error" sx={{ mr: 1 }} />
              <Typography 
                variant="h5"
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                Fire Safety Comes First
              </Typography>
            </div>
            <Typography 
              variant="body1" 
              paragraph 
              sx={{ 
                lineHeight: 1.7,
                fontSize: { xs: '0.95rem', sm: '1rem' }
              }}
            >
              Clogged dryer vents are one of the leading causes of residential
              fires. In multi-family buildings, one neglected vent can
              endanger dozens of residents. Lint buildup is highly flammable,
              and even commercial-grade dryers can pose a fire risk without
              regular maintenance.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                lineHeight: 1.7,
                fontSize: { xs: '0.95rem', sm: '1rem' }
              }}
            >
              Annual vent cleanings are a simple, proactive way to reduce fire
              hazards across your entire property portfolio.
            </Typography>
          </div>
        </Grid>

        {/* Code Compliance Section */}
        <Grid item xs={12} md={6}>
          <div style={{ 
            padding: isMobile ? '1rem' : '1.5rem',
            height: '100%',
            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <Description color="primary" sx={{ mr: 1 }} />
              <Typography variant="h5">
                It's Not Just Smart—It's Code
              </Typography>
            </div>
            <Typography variant="body1" paragraph>
              In Portland and across Oregon, building codes mandate proper
              dryer vent installation and maintenance:
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="2022 Oregon Mechanical Specialty Code (OMSC)"
                  secondary="Requires ducts to be installed to minimize lint buildup and allow for easy cleaning."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Portland BOD 19-08"
                  secondary="Regulates dryer vent terminations, including bans on screens that collect lint."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Manufactured Dwelling Standards"
                  secondary="Require exterior venting and limit duct lengths to reduce clogs and overheating."
                />
              </ListItem>
            </List>
          </div>
        </Grid>

        {/* Tenant Benefits Section */}
        <Grid item xs={12} md={6}>
          <div style={{ 
            padding: isMobile ? '1rem' : '1.5rem',
            height: '100%',
            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <EmojiEmotions color="secondary" sx={{ mr: 1 }} />
              <Typography variant="h5">
                Happier Tenants, Better Performance
              </Typography>
            </div>
            <Typography variant="body1" paragraph>
              Dryer vent cleaning isn't just about safety—it's also good
              business. Clogged vents reduce dryer efficiency, increase
              utility costs, and often lead to tenant complaints about long
              dry times or overheating.
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Keep vents clear to:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Improve drying times" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Lower energy bills" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Extend appliance life" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Reduce maintenance calls" />
              </ListItem>
            </List>
          </div>
        </Grid>

        {/* Service Provider Section */}
        <Grid item xs={12} md={6}>
          <div style={{ 
            padding: isMobile ? '1rem' : '1.5rem',
            height: '100%',
            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <Build color="action" sx={{ mr: 1 }} />
              <Typography variant="h5">
                What to Look for in a Dryer Vent Cleaning Partner
              </Typography>
            </div>
            <Typography variant="body1" paragraph>
              Choose a service provider that understands the unique needs of
              multi-unit properties:
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>✅</ListItemIcon>
                <ListItemText primary="Bulk cleaning services for buildings with 4+ units" />
              </ListItem>
              <ListItem>
                <ListItemIcon>✅</ListItemIcon>
                <ListItemText primary="Before-and-after photos for full transparency" />
              </ListItem>
              <ListItem>
                <ListItemIcon>✅</ListItemIcon>
                <ListItemText primary="Code-compliant technicians familiar with Portland requirements" />
              </ListItem>
              <ListItem>
                <ListItemIcon>✅</ListItemIcon>
                <ListItemText primary="Scheduled maintenance plans to stay ahead" />
              </ListItem>
            </List>
          </div>
        </Grid>

        {/* Don't Wait Section */}
        <Grid item xs={12}>
          <div style={{ 
            margin: '2rem 0',
            padding: isMobile ? '1rem' : '1.5rem',
            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <Warning color="warning" sx={{ mr: 1 }} />
              <Typography variant="h5">Don't Wait for a Problem</Typography>
            </div>
            <Typography variant="body1">
              Dryer vent issues aren't always obvious until it's too late.
              Schedule annual cleanings before peak seasons—Spring or Fall are
              ideal—to stay ahead of risk.
            </Typography>
          </div>
        </Grid>

        {/* Keep Safe Section */}
        <Grid item xs={12}>
          <div style={{ 
            margin: '2rem 0',
            padding: isMobile ? '1rem' : '1.5rem',
            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <Business color="primary" sx={{ mr: 1 }} />
              <Typography variant="h5">
                Keep Your Property Safe & Code-Compliant
              </Typography>
            </div>
            <Typography variant="body1">
              If you manage multi-unit properties in Portland, annual dryer vent
              cleaning isn't optional—it's essential. You're not just removing
              lint—you're protecting your property, your tenants, and your peace
              of mind.
            </Typography>
          </div>
        </Grid>

        <Divider sx={{ my: 4, width: '100%' }} />

        {/* Contact Section */}
        <Grid item xs={12}>
          <div style={{ 
            marginTop: '3rem',
            padding: isMobile ? '2rem 1rem' : '3rem',
            textAlign: 'center',
            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <Typography 
              variant="h4" 
              gutterBottom
              align="center"
              sx={{ fontSize: { xs: '1.75rem', sm: '2rem' } }}
            >
              Ready to Get Started?
            </Typography>
            <Typography 
              variant="h6" 
              gutterBottom
              align="center"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Contact Portland Dryer Mechanic
            </Typography>
            <Typography 
              variant="body1" 
              paragraph
              align="center"
              sx={{ 
                maxWidth: '600px',
                mx: 'auto',
                mb: { xs: 3, sm: 4 }
              }}
            >
              We specialize in multi-unit dryer vent cleaning and help property
              managers stay fire-safe and code-compliant.
            </Typography>

            <Grid 
              container 
              spacing={{ xs: 1, sm: 2 }} 
              justifyContent="center" 
              sx={{ 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center'
              }}
            >
              <Grid item xs={12} sm="auto">
                <Button
                  variant="contained"
                  startIcon={<CalendarToday />}
                  href="#"
                  fullWidth={isMobile}
                  sx={{ mb: { xs: 1, sm: 0 } }}
                >
                  Schedule Assessment
                </Button>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button
                  variant="contained"
                  startIcon={<Phone />}
                  href="tel:5032721113"
                  fullWidth={isMobile}
                  sx={{ mb: { xs: 1, sm: 0 } }}
                >
                  (503) 272-1113
                </Button>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button
                  variant="contained"
                  startIcon={<Email />}
                  href="mailto:Cody@dryermechanic.com"
                  fullWidth={isMobile}
                >
                  Email Us
                </Button>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DryerVentCleaning;